/**
 * API client for the Deals tab.
 *
 * Mirrors the function surface of the standalone dashboard's api/client.js so
 * the ported page works unchanged, but targets this backend's /api/deals/*
 * routes and sends the JWT the rest of the app uses.
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchJson(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      // This backend returns {error}; the reference API returned {detail}.
      detail = body.error ?? body.detail ?? detail;
    } catch {
      // ignore parse errors
    }
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  if (res.status === 204) return null;
  return res.json();
}

export function fetchDealFormsSchema({
  proposal = true,
  longform = true,
  frontline_proposal = true,
  include_catalog = true,
  redelivery_advance = true,
  include_legal = true,
  include_marketing = true,
  discretionary_marketing = false,
  with_options = false,
  proposal_term_length = false,
  publishing_addendum = false,
} = {}) {
  const params = new URLSearchParams();
  params.set('proposal', proposal ? '1' : '0');
  params.set('longform', longform ? '1' : '0');
  params.set('frontline_proposal', frontline_proposal ? '1' : '0');
  params.set('include_catalog', include_catalog ? '1' : '0');
  params.set('redelivery_advance', redelivery_advance ? '1' : '0');
  params.set('include_legal', include_legal ? '1' : '0');
  params.set('include_marketing', include_marketing ? '1' : '0');
  params.set('discretionary_marketing', discretionary_marketing ? '1' : '0');
  params.set('with_options', with_options ? '1' : '0');
  params.set('proposal_term_length', proposal_term_length ? '1' : '0');
  params.set('publishing_addendum', publishing_addendum ? '1' : '0');
  return fetchJson(`/api/deals/schema?${params}`, { cache: 'no-store' });
}

/**
 * Superset schema for the Deals tab: all forms + optional fields enabled.
 * Client filters by toggles / form chips so toggle changes stay instant.
 */
export const DEAL_FORMS_FULL_SCHEMA_PARAMS = Object.freeze({
  proposal: true,
  longform: true,
  frontline_proposal: true,
  include_catalog: true,
  redelivery_advance: true,
  include_legal: true,
  include_marketing: true,
  discretionary_marketing: true,
  with_options: true,
  proposal_term_length: true,
  publishing_addendum: true,
});

let _dealFormsSchemaCache = null;
let _dealFormsSchemaPromise = null;

/** Sync read of the cached full schema (null until first successful fetch). */
export function getDealFormsSchemaCache() {
  return _dealFormsSchemaCache;
}

/** Cached full deal-forms schema (module-level; survives tab remounts). */
export function fetchDealFormsSchemaCached() {
  if (_dealFormsSchemaCache) return Promise.resolve(_dealFormsSchemaCache);
  if (_dealFormsSchemaPromise) return _dealFormsSchemaPromise;
  _dealFormsSchemaPromise = fetchDealFormsSchema(DEAL_FORMS_FULL_SCHEMA_PARAMS)
    .then((data) => {
      _dealFormsSchemaCache = data;
      return data;
    })
    .finally(() => {
      _dealFormsSchemaPromise = null;
    });
  return _dealFormsSchemaPromise;
}

/** Warm the Deals schema cache during idle time (no-op if already loaded). */
export function prefetchDealFormsSchema() {
  return fetchDealFormsSchemaCached().catch(() => null);
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function base64ToUint8Array(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Export filled deal docs; triggers one or more browser downloads. */
export async function exportDealDocuments({ fieldValues, forms, toggles, asZip = false }) {
  const res = await fetch(`${API_BASE}/api/deals/export`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    cache: 'no-store',
    body: JSON.stringify({
      field_values: fieldValues || {},
      forms: forms || ['proposal', 'longform'],
      include_catalog: toggles?.include_catalog !== false,
      redelivery_advance: toggles?.redelivery_advance !== false,
      include_legal: toggles?.include_legal !== false,
      include_marketing: toggles?.include_marketing !== false,
      discretionary_marketing: Boolean(toggles?.discretionary_marketing),
      with_options: Boolean(toggles?.with_options),
      proposal_term_length: Boolean(toggles?.proposal_term_length),
      publishing_addendum: Boolean(toggles?.publishing_addendum),
      as_zip: Boolean(asZip),
    }),
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body.error ?? body.detail ?? detail;
    } catch {
      // ignore
    }
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }

  const contentType = (res.headers.get('Content-Type') || '').toLowerCase();
  const warningsHeader = res.headers.get('X-Encore-Warnings');
  const warningsFromHeader = warningsHeader
    ? warningsHeader.split(' | ').map((s) => s.trim()).filter(Boolean)
    : [];

  // Multi-doc JSON payload -> separate file downloads (default).
  if (contentType.includes('application/json')) {
    const payload = await res.json();
    const files = Array.isArray(payload?.files) ? payload.files : [];
    if (!files.length) throw new Error('No documents generated');
    const filenames = [];
    for (let i = 0; i < files.length; i += 1) {
      const f = files[i];
      const filename = f?.filename || `Deal_Form_${i + 1}.docx`;
      const bytes = base64ToUint8Array(f?.content_base64 || '');
      const blob = new Blob([bytes], {
        type:
          f?.media_type ||
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      if (i > 0) {
        // Give Chrome time between multi-download triggers.
        await new Promise((r) => setTimeout(r, 200));
      }
      triggerBlobDownload(blob, filename);
      filenames.push(filename);
    }
    const warnings = Array.isArray(payload?.warnings)
      ? payload.warnings.map((w) => String(w)).filter(Boolean)
      : warningsFromHeader;
    return { filenames, filename: filenames.join(' + '), warnings };
  }

  // Single docx or legacy zip attachment.
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = /filename="([^"]+)"/i.exec(disposition);
  const filename = match?.[1] || 'Deal_Forms.docx';
  triggerBlobDownload(blob, filename);
  return { filenames: [filename], filename, warnings: warningsFromHeader };
}
